/**
 * TanStack Query cache helpers for Product Sections
 *
 * Product sections are highly dependent on products - they don't have their own
 * async fetch methods. Every time we get a product, we get its sections.
 * This file provides cache utilities for managing product sections data.
 *
 * @module product-sections-queries
 */
import type { useQueryClient } from '@tanstack/react-query';

import { type ProductSection } from '../../entity/api/product-section';

// ============ QUERY KEYS ============

export const productSectionsKeys = {
  all: ['product-sections'] as const,
  byProductId: function (productId: string) {
    return [...productSectionsKeys.all, 'product', productId] as const;
  },
};

// ============ CACHE HELPERS ============

/**
 * Get product sections by product ID from cache
 * Replaces: getStores().productSections.getState().getByProductIdFromStore(productId)
 */
export function getProductSectionsByProductIdFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  productId: string
): ProductSection[] {
  const sections = queryClient.getQueryData<ProductSection[]>(
    productSectionsKeys.byProductId(productId)
  );
  return sections ?? [];
}

/**
 * Set product sections in cache for a specific product
 */
export function setProductSectionsInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  productId: string,
  sections: ProductSection[]
): void {
  queryClient.setQueryData(productSectionsKeys.byProductId(productId), sections);
}

/**
 * Add or update product sections in cache
 * Merges with existing sections
 */
export function updateProductSectionsInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  productId: string,
  sections: ProductSection[]
): void {
  const existingSections = getProductSectionsByProductIdFromCache(queryClient, productId);

  // Create a map for quick lookup
  const sectionsMap = new Map<string, ProductSection>();
  for (const section of existingSections) {
    sectionsMap.set(section._id, section);
  }

  // Add or update sections
  for (const section of sections) {
    sectionsMap.set(section._id, section);
  }

  queryClient.setQueryData(
    productSectionsKeys.byProductId(productId),
    Array.from(sectionsMap.values())
  );
}

/**
 * Update a single product section in cache
 */
export function updateProductSectionInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  productId: string,
  section: ProductSection
): void {
  const existingSections = getProductSectionsByProductIdFromCache(queryClient, productId);

  const updatedSections = existingSections.map(function (s) {
    if (s._id === section._id) {
      return section;
    }
    return s;
  });

  // If section wasn't found, add it
  const found = existingSections.some(function (s) {
    return s._id === section._id;
  });
  if (!found) {
    updatedSections.push(section);
  }

  queryClient.setQueryData(productSectionsKeys.byProductId(productId), updatedSections);
}

/**
 * Remove a product section from cache by its ID
 */
export function removeProductSectionFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  productId: string,
  sectionId: string
): void {
  const existingSections = getProductSectionsByProductIdFromCache(queryClient, productId);

  const updatedSections = existingSections.filter(function (s) {
    return s._id !== sectionId;
  });

  queryClient.setQueryData(productSectionsKeys.byProductId(productId), updatedSections);
}

/**
 * Remove all product sections for a product from cache
 */
export function removeProductSectionsByProductIdFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  productId: string
): void {
  queryClient.removeQueries({
    queryKey: productSectionsKeys.byProductId(productId),
  });
}

/**
 * Invalidate product sections for a specific product
 */
export function invalidateProductSections(
  queryClient: ReturnType<typeof useQueryClient>,
  productId: string
): void {
  void queryClient.invalidateQueries({
    queryKey: productSectionsKeys.byProductId(productId),
  });
}

/**
 * Invalidate all product sections queries
 */
export function invalidateAllProductSections(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({
    queryKey: productSectionsKeys.all,
  });
}
